import React, { useMemo, useState, useEffect } from 'react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import WorkspaceKpis from '../components/workspace/WorkspaceKpis';
import BroadcastService from '../services/BroadcastService';
import StaffService from '../services/StaffService';
import SearchableChecklist from '../components/ui/SearchableChecklist';
import { useUser } from '../store/UserContext';
import { formatDateTime } from '../utils/dateFormat';

const severityOptions = ['info', 'warning', 'critical'];

export default function BroadcastsPage() {
  const { user } = useUser();
  const broadcastService = useMemo(() => new BroadcastService(), []);
  const staffService = useMemo(() => new StaffService(), []);
  const staffList = useMemo(() => staffService.list().filter((member) => member.loginEnabled), [staffService]);
  const [broadcasts, setBroadcasts] = useState(() => broadcastService.list());
  const [activeId, setActiveId] = useState(null);
  const [form, setForm] = useState({
    title: '',
    body: '',
    severity: 'info',
    audience: 'all',
    audienceIds: [],
    effective_from: new Date().toISOString().slice(0, 16),
    expires_at: '',
    ack_required: true,
    ack_phrase: 'AGREE',
    status: 'draft'
  });
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    setBroadcasts(broadcastService.list());
  }, [broadcastService]);

  const selected = broadcasts.find((item) => item.id === activeId) || null;

  const kpis = useMemo(() => {
    const total = broadcasts.length;
    const active = broadcasts.filter((item) => item.status === 'published').length;
    const critical = broadcasts.filter((item) => item.severity === 'critical').length;
    return [
      { id: 'total', label: 'Total broadcasts', value: total },
      { id: 'active', label: 'Published', value: active },
      { id: 'critical', label: 'Critical', value: critical, badge: { label: 'Alert', variant: 'danger' } }
    ];
  }, [broadcasts]);

  const loadBroadcast = (broadcast) => {
    setActiveId(broadcast.id);
    setForm({
      title: broadcast.title,
      body: broadcast.body,
      severity: broadcast.severity,
      audience: broadcast.audience || 'all',
      audienceIds: broadcast.audienceIds || [],
      effective_from: broadcast.effective_from?.slice(0, 16) || '',
      expires_at: broadcast.expires_at?.slice(0, 16) || '',
      ack_required: broadcast.ack_required,
      ack_phrase: broadcast.ack_phrase || 'AGREE',
      status: broadcast.status
    });
  };

  const resetForm = () => {
    setActiveId(null);
    setForm({
      title: '',
      body: '',
      severity: 'info',
      audience: 'all',
      audienceIds: [],
      effective_from: new Date().toISOString().slice(0, 16),
      expires_at: '',
      ack_required: true,
      ack_phrase: 'AGREE',
      status: 'draft'
    });
  };

  const handleSave = () => {
    if (!form.title.trim() || !form.body.trim()) {
      setStatusMessage('Title and message body are required.');
      return;
    }
    if (form.audience === 'selected' && form.audienceIds.length === 0) {
      setStatusMessage('Select at least one staff member for this broadcast.');
      return;
    }
    let next;
    if (selected) {
      next = broadcastService.update(
        {
          ...selected,
          ...form,
          effective_from: form.effective_from ? new Date(form.effective_from).toISOString() : selected.effective_from,
          expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null
        },
        user
      );
    } else {
      next = broadcastService.create(
        {
          ...form,
          effective_from: form.effective_from ? new Date(form.effective_from).toISOString() : new Date().toISOString(),
          expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null
        },
        user
      );
    }
    setBroadcasts(broadcastService.list());
    setActiveId(next?.id || null);
    setStatusMessage('Broadcast saved.');
    window.setTimeout(() => setStatusMessage(''), 2000);
  };

  const handlePublish = () => {
    if (!selected) return;
    if (selected.audience === 'selected' && (!selected.audienceIds || selected.audienceIds.length === 0)) {
      setStatusMessage('Select staff recipients before publishing.');
      return;
    }
    broadcastService.publish(selected.id, user);
    setBroadcasts(broadcastService.list());
    setStatusMessage('Broadcast published.');
    window.setTimeout(() => setStatusMessage(''), 2000);
  };

  const handleExpire = () => {
    if (!selected) return;
    broadcastService.expire(selected.id);
    setBroadcasts(broadcastService.list());
    setStatusMessage('Broadcast expired.');
    window.setTimeout(() => setStatusMessage(''), 2000);
  };

  const previewTitle = form.title || 'Announcement title';
  const previewBody = form.body || 'Announcement message preview will appear here.';
  const selectedCount = form.audience === 'selected' ? form.audienceIds.length : staffList.length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>Broadcast Announcements</h2>
          <p className="sp-muted">Create announcements that require staff acknowledgment.</p>
        </div>
        <Button size="sm" variant="secondary" onClick={resetForm}>
          New broadcast
        </Button>
      </div>

      <WorkspaceKpis items={kpis} />

      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-4">
        <Card className="p-6 space-y-4">
          <div>
            <h3 className="sp-value" style={{ fontWeight: 600 }}>{selected ? 'Edit broadcast' : 'Create broadcast'}</h3>
            <p className="sp-muted">Broadcasts notify all staff and require acknowledgment.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Title</label>
              <input
                className="sp-input mt-2 w-full"
                value={form.title}
                onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
              />
            </div>
            <div>
              <label className="sp-label">Severity</label>
              <select
                className="sp-select mt-2 w-full"
                value={form.severity}
                onChange={(event) => setForm((prev) => ({ ...prev, severity: event.target.value }))}
              >
                {severityOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="sp-label">Effective from</label>
              <input
                type="datetime-local"
                className="sp-input mt-2 w-full"
                value={form.effective_from}
                onChange={(event) => setForm((prev) => ({ ...prev, effective_from: event.target.value }))}
              />
            </div>
            <div>
              <label className="sp-label">Expires at</label>
              <input
                type="datetime-local"
                className="sp-input mt-2 w-full"
                value={form.expires_at}
                onChange={(event) => setForm((prev) => ({ ...prev, expires_at: event.target.value }))}
              />
            </div>
          </div>
          <div>
            <label className="sp-label">Message</label>
            <textarea
              className="sp-textarea mt-2 w-full"
              rows={5}
              value={form.body}
              onChange={(event) => setForm((prev) => ({ ...prev, body: event.target.value }))}
            />
          </div>
          <div className="sp-panel sp-panel--muted p-4 space-y-3">
            <div className="sp-label">Audience</div>
            <div className="flex items-center gap-4 flex-wrap text-sm">
              <label className="sp-inline-toggle">
                <input
                  type="radio"
                  name="broadcast-audience"
                  checked={form.audience === 'all'}
                  onChange={() => setForm((prev) => ({ ...prev, audience: 'all' }))}
                />
                All staff ({staffList.length})
              </label>
              <label className="sp-inline-toggle">
                <input
                  type="radio"
                  name="broadcast-audience"
                  checked={form.audience === 'selected'}
                  onChange={() => setForm((prev) => ({ ...prev, audience: 'selected' }))}
                />
                Selected staff
              </label>
            </div>
            {form.audience === 'selected' && (
              <>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    size="sm"
                    variant="secondary"
                    type="button"
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        audienceIds: staffList.map((member) => member.id)
                      }))
                    }
                  >
                    Select all
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    type="button"
                    onClick={() => setForm((prev) => ({ ...prev, audienceIds: [] }))}
                  >
                    Clear
                  </Button>
                  <span className="text-xs sp-muted">{form.audienceIds.length} selected</span>
                </div>
                <SearchableChecklist
                  items={staffList}
                  selectedIds={form.audienceIds}
                  onToggle={(id) =>
                    setForm((prev) => ({
                      ...prev,
                      audienceIds: prev.audienceIds.includes(id)
                        ? prev.audienceIds.filter((entry) => entry !== id)
                        : [...prev.audienceIds, id]
                    }))
                  }
                  getId={(staff) => staff.id}
                  getLabel={(staff) => staff.name}
                  getMeta={(staff) => staff.role || staff.title || ''}
                  placeholder="Search staff"
                  emptyLabel="No staff available"
                  maxVisible={8}
                />
              </>
            )}
          </div>
          <div className="sp-panel sp-panel--muted p-4 space-y-2">
            <div className="sp-label">Preview</div>
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="sp-value" style={{ fontWeight: 600 }}>{previewTitle}</div>
                <div className="text-xs sp-muted">
                  {form.severity} · Acknowledgment {form.ack_required ? 'required' : 'optional'} · {selectedCount} recipients
                </div>
              </div>
              <Badge variant={form.severity === 'critical' ? 'critical' : form.severity === 'warning' ? 'warning' : 'info'}>
                {form.severity}
              </Badge>
            </div>
            <div className="text-sm whitespace-pre-wrap">{previewBody}</div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <label className="sp-inline-toggle">
              <input
                type="checkbox"
                checked={form.ack_required}
                onChange={(event) => setForm((prev) => ({ ...prev, ack_required: event.target.checked }))}
              />
              Require acknowledgment
            </label>
            <div>
              <label className="sp-label">Acknowledgment phrase</label>
              <input
                className="sp-input mt-2 w-full"
                value={form.ack_phrase}
                onChange={(event) => setForm((prev) => ({ ...prev, ack_phrase: event.target.value }))}
                placeholder="AGREE"
                disabled={!form.ack_required}
              />
            </div>
          </div>
          {statusMessage && <div className="text-xs sp-muted">{statusMessage}</div>}
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" onClick={handleSave}>Save</Button>
            {selected?.status === 'published' ? (
              <Button size="sm" variant="secondary" onClick={handleExpire}>Expire</Button>
            ) : (
              <Button size="sm" variant="secondary" onClick={handlePublish} disabled={!selected}>
                Publish
              </Button>
            )}
          </div>
        </Card>

        <Card className="p-6 space-y-4">
          <div>
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Broadcast history</h3>
            <p className="sp-muted">Click a broadcast to edit or review acknowledgments.</p>
          </div>
          <div className="space-y-3">
            {broadcasts.length === 0 && (
              <div className="sp-panel sp-panel--muted p-4 text-sm">No broadcasts yet.</div>
            )}
            {broadcasts.map((broadcast) => {
              const stats = broadcastService.getAcknowledgmentStats(broadcast);
              return (
                <button
                  key={broadcast.id}
                  type="button"
                  className={`sp-panel w-full p-4 text-left ${broadcast.id === activeId ? '' : 'sp-panel--muted'}`}
                  onClick={() => loadBroadcast(broadcast)}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="sp-value" style={{ fontWeight: 600 }}>{broadcast.title || 'Untitled'}</div>
                    <Badge variant={broadcast.severity === 'critical' ? 'danger' : broadcast.severity === 'warning' ? 'review' : 'info'}>
                      {broadcast.severity}
                    </Badge>
                  </div>
                  <div className="text-xs sp-muted">
                    {broadcast.status} · v{broadcast.version} · {formatDateTime(broadcast.effective_from)}
                  </div>
                  <div className="text-xs sp-muted mt-1">
                    Acknowledged {stats.acknowledged}/{stats.total}
                  </div>
                  <div className="text-xs sp-muted">
                    Audience: {broadcast.audience === 'selected' ? `${stats.total} selected` : 'All staff'}
                  </div>
                </button>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}
